import { Control, Controller } from 'react-hook-form'
import { Dropdown } from 'primereact/dropdown'
import { Button } from 'primereact/button'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

// local imports
import DateUtil from 'utils/DateUtil'
import { IHealthLocationList, IDoctorFilterForm, IDoctorSpecialtyList } from 'types/HealthType'
import DatePickerIcon from 'components/ui/DatePickerIcon'
import LocationMultiselect from 'components/ui/LocationMultiselect'

interface IDoctorFormProps {
  disableResetBtn: boolean
  disableSubmitBtn: boolean
  handleReset: () => void
  control: Control<IDoctorFilterForm>
  specialtyList: IDoctorSpecialtyList[] | undefined
  locationList: IHealthLocationList[] | undefined
}

export default function DoctorFilterForm({
  control,
  specialtyList,
  locationList,
  handleReset,
  disableResetBtn,
  disableSubmitBtn,
}: IDoctorFormProps) {
  const { t } = useTranslation()

  return (
    <>
      {/* Location */}
      <Controller
        name="locationIds"
        control={control}
        render={({ field }) => (
          <LocationMultiselect
            width="15rem"
            wrapperId="doctorLocationWrapper"
            selectProps={{
              id: 'doctorLocationSelect',
              name: 'locationIds',
              filterPlaceholder: t('most-used-words.search'),
              scrollHeight: '160px',
              maxSelectedLabels: 2,
              placeholder: t('input-texts.choose-location'),
              panelStyle: { width: '16rem', maxWidth: '400px' },
              emptyFilterMessage: t('most-used-words.option-not-found'),
              selectedItemsLabel: `${field.value?.length} ${t('input-texts.location-selected')}`,
              optionLabel: 'name',
              optionValue: 'id',
              options: locationList,
              value: field.value,
              onChange: (e) => field.onChange(e.value),
            }}
          />
        )}
      />

      {/* Specialty */}
      <Controller
        name="specialtyId"
        control={control}
        render={({ field }) => (
          <DropdownWrapper>
            <i className="autocomplete-icon p-input-icon-left pi pi-align-left"></i>
            <StyledDropdown
              id="doctorSpecialty"
              filter
              showFilterClear
              filterPlaceholder={t('most-used-words.search')}
              filterBy="name"
              resetFilterOnHide
              emptyMessage={t('most-used-words.option-not-found')}
              emptyFilterMessage={t('most-used-words.option-not-found')}
              appendTo="self"
              scrollHeight="150px"
              name="specialtyId"
              placeholder={t('input-texts.choose-specialty')}
              optionLabel="name"
              optionValue="id"
              options={specialtyList}
              value={field.value}
              onChange={(e) => field.onChange(e.value)}
            />
          </DropdownWrapper>
        )}
      />

      {/* Reservation Date */}
      <Controller
        name="reservationDate"
        control={control}
        render={({ field }) => (
          <DatePickerIcon
            selectOtherMonths
            showOtherMonths
            disabledDays={[]}
            idName="doctorReservationDate"
            defaultDate={field.value}
            minDate={DateUtil.previousMonth()}
            maxDate={DateUtil.futureMonth()}
            onSelectedDateChange={(e) => field.onChange(e.value as Date)}
            label={t('input-texts.appointment-date')}
          />
        )}
      />

      {/* Action Buttons */}
      <ResetButton
        id="filterResetBtn"
        disabled={disableResetBtn}
        type="button"
        onClick={handleReset}
        label={t('most-used-words.reset')}
        className="p-button-outlined"
      />

      <FilterButton id="filterBtn" disabled={disableSubmitBtn} type="submit" label={t('most-used-words.filter')} />
    </>
  )
}

const ResetButton = styled(Button)`
  width: 85px;
  min-width: 60px;
  height: 50px;
  justify-content: center;
  background: #45bfe5;
  border-radius: 10px;

  :disabled {
    opacity: 0.4;
  }
`
const FilterButton = styled(Button)`
  width: 85px;
  min-width: 60px;
  height: 50px;
  justify-content: center;
  background: #45bfe5;
  border-radius: 10px;
`

const DropdownWrapper = styled.div`
  position: relative;
  .autocomplete-title {
    position: absolute;
    left: 35px;
  }

  .autocomplete-icon {
    position: absolute;
    left: 8px;
    top: 16px;
    z-index: 1;
    color: #a8a8a8;
  }

  .selectedVal,
  .p-dropdown .p-dropdown-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .selectedVal {
    margin-top: 0.5rem;
    font-weight: 700;
  }

  .p-dropdown .p-dropdown-label {
    padding-left: 2rem;
    padding-right: 0;
  }
`

const StyledDropdown = styled(Dropdown)`
  // width: 100%;
  width: 15rem;
  height: 50px;
  border-radius: 10px;
  align-items: center;

  .p-dropdown-panel {
    z-index: 1004 !important;
    position: absolute !important;
    left: 0px !important;
    top: 52px !important;
    min-width: 100% !important;
    min-height: 100% !important;
    background: #ffffff !important;
    transform-origin: center top !important;
  }

  .p-dropdown-items-wrapper {
    max-height: 250px !important;
  }

  @media (max-width: 1400px) {
    max-width: 14rem;
  }

  @media (max-width: 1200px) {
    max-width: 11rem;
  }
`
